// FICHIER : frontend/src/pages/Recruteur.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavLogo from '../components/NavLogo';
import axios from "axios";

// ── Configuration ──
const API          = "https://ark-recruitment-backend.onrender.com/api";
// ── Labels des phases d'entretien ──
const PHASE_LABELS = {
  profilage:       "Profilage",
  comportementale: "Compétences Comportementales",
  technique:       "Compétences Techniques",
  cognitive:       "Compétences Cognitives",
};

// ── Couleur selon le score ──
function scoreColor(score, max=100) {
  const pct = (score / max) * 100;
  if (pct >= 80) return 'var(--success)';
  if (pct >= 60) return 'var(--highlight)';
  return 'var(--error)';
}

// ── Style badge décision ──
function decisionStyle(decision) {
  if (decision === 'Retenu'    || decision === 'Prioritaire') return { color:'var(--success)', background:'rgba(22,163,74,0.06)', border:'1px solid rgba(22,163,74,0.12)' };
  if (decision === 'En attente'|| decision === 'Secondaire')  return { color:'var(--highlight)',        background:'rgba(232,200,61,0.10)',  border:'1px solid rgba(232,200,61,0.25)'  };
  return { color:'var(--error)', background:'rgba(220,38,38,0.04)', border:'1px solid rgba(220,38,38,0.15)' };
}

// ── Composant principal Recruteur ──
export default function Recruteur() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ── Détection mobile ──
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── États données ──
  const [candidates,      setCandidates]      = useState([]);
  const [selected,        setSelected]        = useState(null);
  const [rapport,         setRapport]         = useState(null);
  const [loadingRapport,  setLoadingRapport]  = useState(false);
  const [onglet,          setOnglet]          = useState('rapport');

  // ── Chargement candidats au montage ──
  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const companyId = localStorage.getItem('company_id') || '';
      const url = companyId ? `${API}/candidates?company_id=${companyId}` : `${API}/candidates`;
      const res = await axios.get(url);
      setCandidates(res.data);
    } catch (e) {}
  };

  const fetchRapport = async (analyseId) => {
    setLoadingRapport(true);
    setRapport(null);
    try {
      const res = await axios.get(`${API}/rapport/${analyseId}`);
      setRapport(res.data);
    } catch (e) { setRapport(null); }
    finally { setLoadingRapport(false); }
  };

  const ouvrirCandidat = (c) => {
    setSelected(c);
    setOnglet('rapport');
    fetchRapport(c.id);
  };

  // ── Styles réutilisables ──
  const card = { background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding: isMobile ? '20px 16px' : '28px' };
  const sectionTitle = { fontFamily:'Syne,sans-serif', fontSize:15, fontWeight:700, marginBottom:16, color:'var(--text)' };

  // ════════════════════════════════════════
  // ── VUE FICHE CANDIDAT ──
  // ════════════════════════════════════════
  if (selected) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', color:'var(--text)', fontFamily:'DM Sans,sans-serif' }}>

      {/* Nav */}
      <nav style={{ position:'sticky', top:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'space-between', padding: isMobile ? '0 20px' : '0 48px', height:60, background:'var(--nav-bg)', borderBottom:'1px solid var(--border)' }}>
        <NavLogo/>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          {/* Nom du fichier */}
          <div style={{ fontFamily:'Syne,sans-serif', fontWeight:700, fontSize: isMobile ? 13 : 15 }}>
            {selected.filename}
          </div>
          {/* Badge décision */}
          <span style={{ ...decisionStyle(selected.niveau), padding:'4px 12px', borderRadius:20, fontSize:11, fontFamily:'DM Mono,monospace', fontWeight:600, letterSpacing:0.5 }}>
            {selected.niveau}
          </span>
        </div>
        <button onClick={() => { setSelected(null); setRapport(null); }} style={{ color:'var(--muted)', fontSize:12, fontFamily:'DM Mono,monospace', background:'none', border:'none', cursor:'pointer', letterSpacing:0.5 }}>
          ← Retour
        </button>
      </nav>

      {/* Onglets */}
      <div style={{ display:'flex', gap:0, borderBottom:'1px solid var(--border)', padding: isMobile ? '0 20px' : '0 48px', background:'var(--bg2)' }}>
        {[
          { key:'rapport',   label:'Rapport & Scores'    },
          { key:'entretien', label:'Questions & Réponses' },
        ].map(o => (
          <button
            key={o.key}
            onClick={() => setOnglet(o.key)}
            style={{ padding:'14px 20px', fontSize:13, fontFamily:'DM Mono,monospace', fontWeight:500, letterSpacing:0.5, background:'none', border:'none', borderBottom:`2px solid ${onglet === o.key ? 'var(--accent)' : 'transparent'}`, color: onglet === o.key ? 'var(--accent2)' : 'var(--muted)', cursor:'pointer' }}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding: isMobile ? '24px 16px' : '40px 24px', display:'flex', flexDirection:'column', gap:20 }}>

        {/* ── ONGLET RAPPORT ── */}
        {onglet === 'rapport' && (
          <>
            {/* Analyse CV */}
            <div style={card}>
              <div style={sectionTitle}>Analyse CV</div>
              <div style={{ display:'flex', alignItems:'center', gap:24, marginBottom:20, flexWrap:'wrap' }}>
                {/* Score CV */}
                <div style={{ textAlign:'center', flexShrink:0 }}>
                  <div style={{ fontFamily:'Syne,sans-serif', fontSize:48, fontWeight:800, color:scoreColor(selected.score), lineHeight:1 }}>{selected.score}</div>
                  <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--muted)', letterSpacing:1, marginTop:4 }}>Score CV / 100</div>
                </div>
                <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.7, flex:1 }}>{selected.resume_candidat}</p>
              </div>

              {/* Points forts / vigilance */}
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:12 }}>
                <div style={{ background:'rgba(22,163,74,0.04)', border:'1px solid rgba(22,163,74,0.12)', borderRadius:8, padding:'16px' }}>
                  <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--success)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:10 }}>Points forts</div>
                  {(selected.points_forts || []).map((p, i) => (
                    <div key={i} style={{ display:'flex', gap:8, fontSize:12, color:'var(--text)', lineHeight:1.6, marginBottom:4 }}>
                      <span style={{ color:'var(--success)', flexShrink:0 }}>✓</span>{p}
                    </div>
                  ))}
                </div>
                <div style={{ background:'rgba(220,38,38,0.04)', border:'1px solid rgba(220,38,38,0.12)', borderRadius:8, padding:'16px' }}>
                  <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--error)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:10 }}>Points de vigilance</div>
                  {(selected.points_faibles || []).map((p, i) => (
                    <div key={i} style={{ display:'flex', gap:8, fontSize:12, color:'var(--text)', lineHeight:1.6, marginBottom:4 }}>
                      <span style={{ color:'var(--error)', flexShrink:0 }}>!</span>{p}
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommandation CV */}
              <div style={{ background:'var(--accent-08)', border:'1px solid var(--border)', borderRadius:8, padding:'16px' }}>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--accent2)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:8 }}>Recommandation CV</div>
                <p style={{ fontSize:13, color:'var(--text)', lineHeight:1.7 }}>{selected.recommandation}</p>
              </div>
            </div>

            {/* Chargement rapport */}
            {loadingRapport && (
              <div style={{ ...card, textAlign:'center', color:'var(--muted)', fontFamily:'DM Mono,monospace', fontSize:12, letterSpacing:1 }}>
                Chargement du rapport...
              </div>
            )}

            {/* Pas de rapport */}
            {!loadingRapport && !rapport && (
              <div style={{ ...card, textAlign:'center', color:'var(--muted)', fontFamily:'DM Mono,monospace', fontSize:12, letterSpacing:1 }}>
                Aucun rapport d'entretien disponible.
              </div>
            )}

            {/* Rapport complet */}
            {rapport && (
              <>
                {/* Score global */}
                <div style={card}>
                  <div style={sectionTitle}>Rapport d'entretien</div>
                  <div style={{ display:'flex', alignItems:'center', gap:24, marginBottom:24, flexWrap:'wrap' }}>
                    <div style={{ textAlign:'center', flexShrink:0 }}>
                      <div style={{ fontFamily:'Syne,sans-serif', fontSize:48, fontWeight:800, color:scoreColor(rapport.score_total), lineHeight:1 }}>{rapport.score_total}</div>
                      <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--muted)', letterSpacing:1, marginTop:4 }}>Score global / 100</div>
                    </div>
                    <div>
                      <span style={{ ...decisionStyle(rapport.decision), padding:'5px 14px', borderRadius:20, fontSize:12, fontFamily:'DM Mono,monospace', fontWeight:600 }}>
                        {rapport.decision}
                      </span>
                      <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.7, marginTop:10, maxWidth:500 }}>{rapport.resume_executif}</p>
                    </div>
                  </div>

                  {/* Scores par phase */}
                  <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:10, marginBottom:20 }}>
                    {[
                      { label:'Profilage',                  score:rapport.score_profilage        },
                      { label:'Compétences Comportementales', score:rapport.score_comportementale },
                      { label:'Compétences Techniques',      score:rapport.score_technique        },
                      { label:'Compétences Cognitives',      score:rapport.score_cognitive        },
                    ].map(p => (
                      <div key={p.label} style={{ border:'1px solid var(--border)', borderRadius:8, padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:13, color:'var(--muted)' }}>{p.label}</span>
                        <span style={{ fontFamily:'Syne,sans-serif', fontWeight:800, fontSize:18, color:scoreColor(p.score, 25) }}>{p.score}/25</span>
                      </div>
                    ))}
                  </div>

                  {/* Points forts / vigilance globaux */}
                  <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12 }}>
                    <div style={{ background:'rgba(22,163,74,0.04)', border:'1px solid rgba(22,163,74,0.12)', borderRadius:8, padding:'16px' }}>
                      <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--success)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:10 }}>Points forts globaux</div>
                      {(rapport.points_forts_globaux || []).map((p, i) => (
                        <div key={i} style={{ display:'flex', gap:8, fontSize:12, color:'var(--text)', lineHeight:1.6, marginBottom:4 }}>
                          <span style={{ color:'var(--success)', flexShrink:0 }}>✓</span>{p}
                        </div>
                      ))}
                    </div>
                    <div style={{ background:'rgba(220,38,38,0.04)', border:'1px solid rgba(220,38,38,0.12)', borderRadius:8, padding:'16px' }}>
                      <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--error)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:10 }}>Points de vigilance</div>
                      {(rapport.points_vigilance || []).map((p, i) => (
                        <div key={i} style={{ display:'flex', gap:8, fontSize:12, color:'var(--text)', lineHeight:1.6, marginBottom:4 }}>
                          <span style={{ color:'var(--error)', flexShrink:0 }}>!</span>{p}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recommandation recruteur */}
                <div style={card}>
                  <div style={sectionTitle}>Recommandation recruteur</div>
                  <div style={{ background:'var(--accent-08)', border:'1px solid var(--border)', borderRadius:8, padding:'16px', marginBottom:20 }}>
                    <p style={{ fontSize:13, color:'var(--text)', lineHeight:1.7 }}>{rapport.recommandation_recruteur}</p>
                  </div>

                  {/* Questions pour l'appel */}
                  <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--accent2)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:12 }}>
                    Questions pour l'appel téléphonique
                  </div>
                  <div style={{ background:'rgba(232,200,61,0.08)', border:'1px solid rgba(232,200,61,0.15)', borderRadius:8, padding:'16px' }}>
                    {(rapport.questions_appel || []).map((q, i) => (
                      <div key={i} style={{ display:'flex', gap:10, fontSize:13, color:'var(--text)', lineHeight:1.6, marginBottom:10 }}>
                        <span style={{ fontFamily:'DM Mono,monospace', color:'var(--highlight)', fontWeight:700, flexShrink:0 }}>{i+1}.</span>{q}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ── ONGLET Q&A ── */}
        {onglet === 'entretien' && (
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            {loadingRapport && (
              <div style={{ ...card, textAlign:'center', color:'var(--muted)', fontFamily:'DM Mono,monospace', fontSize:12 }}>Chargement...</div>
            )}
            {!loadingRapport && !rapport && (
              <div style={{ ...card, textAlign:'center', color:'var(--muted)', fontFamily:'DM Mono,monospace', fontSize:12 }}>Aucun entretien disponible.</div>
            )}
            {rapport && rapport.historique_complet && Object.keys(PHASE_LABELS).map(phase => {
              const echanges = rapport.historique_complet[phase] || [];
              if (echanges.length === 0) return null;
              return (
                <div key={phase} style={card}>
                  {/* Titre phase */}
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--accent)', display:'inline-block', flexShrink:0 }}/>
                    <div style={{ fontFamily:'Syne,sans-serif', fontSize:14, fontWeight:700 }}>{PHASE_LABELS[phase]}</div>
                  </div>

                  {/* Échanges */}
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    {echanges.map((echange, i) => (
                      <div key={i} style={{ border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
                        {/* Question */}
                        <div style={{ background:'var(--accent-08)', padding:'12px 16px' }}>
                          <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--accent2)', letterSpacing:1, marginBottom:6 }}>Question {i+1}</div>
                          <p style={{ fontSize:13, color:'var(--text)', lineHeight:1.6 }}>{echange.question}</p>
                        </div>
                        {/* Réponse */}
                        <div style={{ background:'var(--bg3)', padding:'12px 16px' }}>
                          <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--muted)', letterSpacing:1, marginBottom:6 }}>Réponse du candidat</div>
                          <p style={{ fontSize:13, color:'var(--text)', lineHeight:1.6 }}>{echange.reponse}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ════════════════════════════════════════
  // ── VUE LISTE CANDIDATS ──
  // ════════════════════════════════════════
  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', color:'var(--text)', fontFamily:'DM Sans,sans-serif' }}>

      {/* Nav */}
      <nav style={{ position:'sticky', top:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'space-between', padding: isMobile ? '0 20px' : '0 48px', height:60, background:'var(--nav-bg)', borderBottom:'1px solid var(--border)' }}>
        <NavLogo/>
        <div style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--muted)', letterSpacing:1 }}>
          ESPACE <span style={{ color:'var(--accent2)' }}>RECRUTEUR</span>
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{ color:'var(--muted)', fontSize:12, fontFamily:'DM Mono,monospace', background:'none', border:'none', cursor:'pointer', letterSpacing:0.5 }}
        >
          ← Retour
        </button>
      </nav>

      <div style={{ maxWidth:900, margin:'0 auto', padding: isMobile ? '24px 16px' : '40px 24px' }}>
        <div style={card}>

          {/* Titre */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
            <div>
              <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--muted)', letterSpacing:2, textTransform:'uppercase', marginBottom:6 }}>
                Candidats analysés
              </div>
              <div style={{ fontFamily:'Syne,sans-serif', fontSize:20, fontWeight:800 }}>
                {candidates.length} candidat{candidates.length > 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Liste vide */}
          {candidates.length === 0 && (
            <div style={{ textAlign:'center', padding:'48px 0', color:'var(--muted)', fontFamily:'DM Mono,monospace', fontSize:12, letterSpacing:1 }}>
              Aucun candidat pour l'instant.
            </div>
          )}

          {/* Liste candidats */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {candidates.map(c => (
              <div
                key={c.id}
                onClick={() => ouvrirCandidat(c)}
                style={{ border:'1px solid var(--border)', borderRadius:8, padding: isMobile ? '14px' : '16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', background:'var(--bg3)', gap:12 }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:16, flex:1, minWidth:0 }}>
                  {/* Score */}
                  <div style={{ fontFamily:'Syne,sans-serif', fontSize:24, fontWeight:800, color:scoreColor(c.score), flexShrink:0 }}>{c.score}</div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:14, marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.filename}</div>
                    <div style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--muted)', letterSpacing:0.5 }}>
                      {c.job_description} — {new Date(c.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
                  <span style={{ ...decisionStyle(c.niveau), padding:'4px 12px', borderRadius:20, fontSize:11, fontFamily:'DM Mono,monospace', fontWeight:600 }}>
                    {c.niveau}
                  </span>
                  <span style={{ color:'var(--muted)', fontSize:16 }}>→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}